import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Input, Button, Editor, ImageInput, Textarea } from "components/common/common";
import s from "./CreateArticle.module.css";
import { updateArticle, createArticle } from "store/articlesSlice";

const CreateArticle = () => {
  const { hash } = useParams();
  const { state } = useLocation();
  const isEditing = state?.isEditing || false;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [editorData, setEditorData] = useState(state?.articleData || {});
  const [title, setTitle] = useState(state?.title || "");
  // userDescription - для пользователей и статики, shortDescription - для админки
  const [userDescription, setUserDescription] = useState(state?.userDescription || state?.description || "");
  const [shortDescription, setShortDescription] = useState(state?.shortDescription || "");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(state?.cover ? state.cover : null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  const handleImageChange = (file) => {
    const MAX_FILE_SIZE = 2 * 1024 * 1024;

    if (file instanceof File) {
      if (file.size > MAX_FILE_SIZE) {
        setError("Размер файла слишком большой. Максимальный размер: 2MB");
        setCoverPreview(null);
        setCoverFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setCoverFile(file);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      const previewUrl = URL.createObjectURL(file);
      setCoverPreview(previewUrl);
      setError(null);
    } else {
      setError("Неверный формат файла");
    }
  };

  const handlePreviewClear = () => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(null);
    setCoverFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Название статьи обязательно");
      return;
    }

    if (!userDescription.trim()) {
      setError("Описание для пользователя обязательно");
      return;
    }

    if (!shortDescription.trim()) {
      setError("Описание для администрирования обязательно");
      return;
    }

    // Даем редактору время сохранить последние изменения
    await new Promise((resolve) => setTimeout(resolve, 200));

    if (!editorData?.blocks?.length) {
      setError("Содержание статьи не может быть пустым");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", JSON.stringify(editorData));
      formData.append("shortDescription", shortDescription);
      formData.append("userDescription", userDescription);
      if (coverFile) formData.append("cover", coverFile);
      if (isEditing) formData.append("hash", hash);

      if (isEditing) {
        await dispatch(updateArticle(formData)).unwrap();
      } else {
        await dispatch(createArticle(formData)).unwrap();
      }

      navigate("/admin/articles");
    } catch (err) {
      setError(err.message || "Ошибка при сохранении статьи");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={s.container}>
      <h1>{isEditing ? "Редактировать статью" : "Создать новую статью"}</h1>

      <div className={s.collapsibleSection}>
        <div
          className={s.collapsibleHeader}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h2>Основные данные</h2>
          <span>{isCollapsed ? "▼" : "▲"}</span>
        </div>
        {!isCollapsed && (
          <div className={s.collapsibleContent}>
            {error && <div className={s.error}>{error}</div>}

            <Input
              type="text"
              label="Название статьи"
              value={title}
              onChange={setTitle}
              placeholder="Введите название статьи"
              className="page-title-input"
              required
            />

            <ImageInput
              onChange={handleImageChange}
              label="Обложка статьи"
              required={!isEditing}
              accept="image/*"
              preview={coverPreview}
              onPreviewClear={handlePreviewClear}
              ref={fileInputRef}
            />

            <Textarea
              label="Описание для пользователя"
              value={userDescription}
              onChange={setUserDescription}
              placeholder="Введите описание для пользователя"
              rows={3}
            />

            <Textarea
              label="Описание для администрирования"
              value={shortDescription}
              onChange={setShortDescription}
              placeholder="Введите описание для администрирования"
              rows={3}
            />
          </div>
        )}
      </div>

      <div className={s.editorcontainer}>
        <Editor
          data={editorData}
          onChange={setEditorData}
          holder="editorjs-create"
        />
        <Button
          onClick={handleSubmit}
          label={
            isLoading
              ? isEditing
                ? "Сохранение изменений..."
                : "Сохранение..."
              : isEditing
              ? "Сохранить изменения"
              : "Сохранить статью"
          }
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default CreateArticle;
